import { useCallback, useState } from 'react';
import axios from 'axios';
import client from '../../../shared/api/client';
import { isJsonPayload } from '../../../shared/api/payload';
import { actionErrorText } from '../../../shared/toastHelper';
import { toast } from 'react-toastify';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import { offsetDateForServer } from '../../../shared/timeZoneLogic';

const ERROR_TOAST_OPTS = {
  position: toast.POSITION.TOP_CENTER,
  autoClose: 5000,
  hideProgressBar: true,
  draggable: false,
};

const SUCCESS_TOAST_OPTS = {
  position: toast.POSITION.TOP_CENTER,
  autoClose: 3000,
  hideProgressBar: true,
  draggable: false,
};

const HOLD_CREATED_RELOAD_DELAY_MS = 1200;

const artistChoiceAttributes = ({
  artistId, newArtistName, spotifyId, externalUrl, spotifyTakeover,
}) => (newArtistName
  ? {
    artist_attributes: {
      name: newArtistName,
      ...(spotifyId ? { spotify_id: spotifyId } : {}),
      ...(externalUrl ? { external_url: externalUrl } : {}),
      ...(spotifyTakeover ? { spotify_takeover: true } : {}),
    },
  }
  : { artistable_id: artistId });

const copyPayload = ({ keepCalendarEvent, artist }) => ({
  ...(keepCalendarEvent ? { keep_calendar_event: true } : {}),
  ...(artist ? { primary_performer: artistChoiceAttributes(artist) } : {}),
});

export default function useV3OfferActions() {
  const {
    csrfToken, team, offer, editorVariant, dispatch,
  } = useV3OfferContext();
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const handlePreview = useCallback((offerId) => {
    const id = typeof offerId === 'number' ? offerId : offer?.id;
    if (!id) return;
    window.open(`/teams/${team.id}/offers/v3/${id}`, '_blank');
  }, [team, offer]);

  const handleSubmitCopy = useCallback(async (choice) => {
    if (!offer?.id) return;
    setIsCopying(true);
    try {
      const response = await client.post(
        `/teams/${team.id}/offers/v3/${offer.id}/copy.json`,
        copyPayload(choice),
      );
      const newOffer = response.data;
      if (!isJsonPayload(newOffer)) {
        toast.error("Couldn't copy this offer. Please reload the page and try again.", ERROR_TOAST_OPTS);
        return;
      }
      toast.success('Offer copied', SUCCESS_TOAST_OPTS);
      setCopyModalOpen(false);
      if (newOffer?.id) {
        const path = editorVariant === 'v3_simple' ? 'offers/v3_simple' : 'offers/v3';
        window.location.href = `/teams/${team.id}/${path}/${newOffer.id}/edit`;
      }
    } catch (e) {
      toast.error(actionErrorText(e, 'Failed to copy offer'), ERROR_TOAST_OPTS);
    } finally {
      setIsCopying(false);
    }
  }, [team, offer, editorVariant]);

  const handleCopy = useCallback(() => {
    if (offer?.id) setCopyModalOpen(true);
  }, [offer]);

  const handleCancelCopy = useCallback(() => setCopyModalOpen(false), []);

  const handleDownload = useCallback(() => {
    if (!offer?.id) return;
    window.open(`/teams/${team.id}/offers/v3/${offer.id}/preview.pdf`, '_blank');
  }, [team, offer]);

  const handleDelete = useCallback(async () => {
    if (!offer?.id) return;
    if (typeof window !== 'undefined' && !window.confirm(
      `Delete the offer for ${offer.performer_name || 'this artist'}? This cannot be undone.`
    )) return;
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    try {
      const response = await axios.delete(`/teams/${team.id}/offers/v3/${offer.id}.json`);
      if (!isJsonPayload(response.data)) {
        toast.error("Couldn't delete this offer. Please reload the page and try again.", ERROR_TOAST_OPTS);
        return;
      }
      window.location.href = `/teams/${team.id}/offers`;
    } catch (e) {
      toast.error(actionErrorText(e, 'Failed to delete offer'), ERROR_TOAST_OPTS);
    }
  }, [team, offer, csrfToken]);

  const handleChangeActType = useCallback(async (actType) => {
    if (!offer?.id) return;
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    try {
      const payload = offer.primary_performer?.id
        ? {
          v3_offer: {
            primary_performer_attributes: {
              id: offer.primary_performer.id,
              act_type: actType,
            },
          },
        }
        : { v3_offer: { act_type: actType } };
      const { data } = await axios.patch(`/teams/${team.id}/offers/v3/${offer.id}`, payload);
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: { ...offer, ...data } });
    } catch (e) {
      toast.error('Failed to update act type', ERROR_TOAST_OPTS);
    }
  }, [team, offer, csrfToken, dispatch]);

  const handleEditPrimaryPerformer = useCallback(async (pick) => {
    if (!offer?.id) return;
    const existingId = offer.primary_performer?.id;
    const performerAttributes = {
      ...(existingId ? { id: existingId } : {}),
      artistable_type: 'Artist',
      ...artistChoiceAttributes(pick),
    };
    try {
      const { data } = await client.patch(
        `/teams/${team.id}/offers/v3/${offer.id}`,
        { v3_offer: { primary_performer_attributes: performerAttributes } },
      );
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: { ...offer, ...data } });
    } catch (e) {
      const detail = Array.isArray(e?.response?.data)
        ? e.response.data.join(', ')
        : (typeof e?.response?.data === 'string' ? e.response.data : null);
      toast.error(detail ? `Failed to update artist: ${detail}` : 'Failed to update artist', ERROR_TOAST_OPTS);
    }
  }, [team, offer, dispatch]);

  const handleCreateHold = useCallback(async ({
    title, buyerId, holdGroup, selectedRooms,
  }) => {
    if (!offer?.id) return;
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const entries = (holdGroup?.hold_group_entries || []).filter((e) => !e._destroy);
    if (entries.length === 0 || !selectedRooms || selectedRooms.length === 0) return;

    const roomIds = selectedRooms.map((r) => r.value);
    const venueOwnershipId = selectedRooms[0]?.venueOwnershipId;
    const artistId = offer.primary_performer?.artistable_id
      || offer.primary_performer?.artistable?.id
      || null;

    const payload = {
      hold: {
        team_id: team.id,
        title,
        venue_id: offer.venue_id || offer.venue?.id || null,
        venue_ownership_id: venueOwnershipId,
        buyer_id: buyerId || null,
        artist_ids: artistId ? [artistId] : [],
        // CalendarEvent#pipeline is validated `presence: true` and Hold has
        // `before_create :build_create_hold_group_pipeline_event`. Without
        // pipeline_attributes the save fails. Mirrors what
        // EventForm/actions/eventFormActionCreators.createHoldGroupParams
        // sends when there is no existing pipeline to reuse.
        pipeline_attributes: { team_id: team.id, state: 'calendar_event' },
        hold_positions_attributes: entries.map((e) => ({
          start_time: offsetDateForServer(e.hold.start_time),
          position: e.hold.position || 1,
          is_target: !!e.hold.is_target,
          venue_ownership_id: venueOwnershipId,
          room_ids: roomIds,
        })),
      },
    };

    let newHoldId = null;
    try {
      const { data: holdPositions } = await axios.post(
        `/teams/${team.id}/hold_groups`,
        payload,
      );
      // HoldPosition#to_calendar returns `id` (the Hold's CalendarEvent id via
      // STI) — there is no `calendar_event_id` key in the payload. Without
      // this we silently skipped the link PATCH and the offer stayed
      // detached from the new hold across refreshes.
      newHoldId = Array.isArray(holdPositions)
        ? (holdPositions[0]?.id || holdPositions[0]?.calendar_event_id)
        : null;
    } catch (e) {
      // Surface the server's validation messages when available — the
      // generic toast hides what actually failed and forces a Network-tab
      // dive.
      const serverErrors = e?.response?.data;
      const detail = Array.isArray(serverErrors)
        ? serverErrors.join(', ')
        : (typeof serverErrors === 'string' ? serverErrors : null);
      toast.error(detail ? `Failed to create hold: ${detail}` : 'Failed to create hold', ERROR_TOAST_OPTS);
      throw e;
    }

    try {
      if (newHoldId) {
        // Link the WHOLE lineup to the new hold's CalendarEvent, not just the
        // active offer. Lineup grouping switches from the shared v3_budget to
        // `calendar_event.v3_offers` the moment one offer is linked, so linking
        // only the active offer would drop every sibling (e.g. the headliner)
        // from the lineup. Link siblings first, the active offer last, so its
        // blueprint response reflects the now-complete lineup.
        const siblingIds = (offer.lineup || [])
          .map((l) => l.id)
          .filter((id) => id && id !== offer.id);
        await Promise.all(siblingIds.map((id) => axios.patch(
          `/teams/${team.id}/offers/v3/${id}`,
          { v3_offer: { calendar_event_id: newHoldId } },
        )));
        const { data: linkedOffer } = await axios.patch(
          `/teams/${team.id}/offers/v3/${offer.id}`,
          { v3_offer: { calendar_event_id: newHoldId } },
        );
        dispatch({ type: V3_ACTIONS.SET_OFFER, payload: { ...offer, ...linkedOffer } });
      }
      toast.success('Hold created', SUCCESS_TOAST_OPTS);
      setTimeout(() => window.location.reload(), HOLD_CREATED_RELOAD_DELAY_MS);
    } catch (e) {
      // The hold itself was created above; only the offer linking failed.
      // Treat it as created (no rollback) and do not re-throw.
      toast.error('Hold created, but some offers could not be linked.', ERROR_TOAST_OPTS);
    }
  }, [team, offer, csrfToken, dispatch]);

  return {
    handlePreview,
    handleCopy,
    copyModalOpen,
    isCopying,
    handleSubmitCopy,
    handleCancelCopy,
    handleDownload,
    handleDelete,
    handleChangeActType,
    handleEditPrimaryPerformer,
    handleCreateHold,
  };
}
